(() => {
  if (customElements.get('predictive-search')) {
    return;
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);
      const debouncedOnChangeTrack = debounce((event) => {
        this.trackSearchEvent(event.target.value);
      }, 2000);

      this.detailsElement = this.closest('details');
      this.summaryElement = this.detailsElement.querySelector('summary');
      this.form = this.querySelector('form');
      this.resultsWrapper = this.querySelector('[data-results]');
      this.clearButton = this.querySelector('[data-clear]');
      this.closeButton = this.querySelector('[data-close]');
      this.overlay = document.querySelector('[data-overlay]');
      this.input = this.querySelector('input[type="search"]');
      this.addEventListener('input', debouncedOnChange.bind(this));
      this.addEventListener('input', debouncedOnChangeTrack.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.clearButton.addEventListener('click', this.onClear.bind(this));
      this.closeButton.addEventListener('click', this.onClose.bind(this));
      this.overlay.addEventListener('click', this.onClose.bind(this));
      this.summaryElement.addEventListener(
        'click',
        this.onSummaryClick.bind(this)
      );
      this.addEventListener('transitionend', this.focusInput.bind(this));
    }

    focusInput() {
      this.input.focus();
    }

    close() {
      this.detailsElement.classList.remove('is-open');
      this.closeAnimation();
    }

    onSummaryClick(e) {
      const isOpen = this.detailsElement.hasAttribute('open');

      if (isOpen) e.preventDefault();
      isOpen ? this.onClose(e) : this.onOpen();
    }

    onOpen() {
      setTimeout(() => {
        this.detailsElement.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
      }, 100);
    }

    onClose(e) {
      e.preventDefault();
      this.close();
      document.body.classList.remove('overflow-hidden');
    }

    onClear(e) {
      e.preventDefault();

      this.input.value = '';
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.focusInput();
    }

    onSubmit(e) {
      e.preventDefault();
    }

    onChange(e) {
      if (e.target.value === '') return this.clearResults();

      const queryString = new URLSearchParams(
        new FormData(this.form)
      ).toString();
      let requestResponse;

      this.setPerformed(true);
      this.setLoading(true);

      fetch(`${window.Shopify.routes.root}search/suggest?${queryString}`)
        .then((response) => {
          requestResponse = response;
          return response.text();
        })
        .then((text) => {
          if (!requestResponse.ok) {
            throw new Error(`${requestResponse.status}: ${text}`);
          }

          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;

          this.removeListeners();
          this.resultsWrapper.innerHTML = resultsMarkup;
          this.setElements();
          this.setListeners();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => this.setLoading(false));
    }

    trackSearchEvent(query) {
      if (!window.analytics || !window.sbjsData || query === '') return;

      window.analytics.track(
        'Products Searched',
        Object.assign(
          {
            query: query,
            ...(gaClientIDValue && { ga_client_id: gaClientIDValue }),
            ...(gaSessionIDValue && { ga_session_id: gaSessionIDValue }),
          },
          window.sbjsData
        )
      );
    }

    clearResults() {
      this.removeListeners();
      this.tabs = undefined;
      this.tabButtons = undefined;
      this.resultsWrapper.innerHTML = '';
      this.setPerformed(false);
      this.setLoading(false);
    }

    setPerformed(flag) {
      this.setAttribute('data-search-performed', flag);
    }

    setLoading(flag) {
      this.setAttribute('data-loading', flag);
    }

    setElements() {
      this.tabButtons = this.querySelectorAll('[data-tab-button]');
      this.tabs = this.querySelectorAll('[data-tab]');
    }

    removeListeners() {
      if (!this.tabButtons) return;

      this.tabButtons.forEach((button) => {
        button.removeEventListener('click', this.onTabButtonClick.bind(this));
      });
    }

    setListeners() {
      if (!this.tabButtons) return;

      this.tabButtons.forEach((button) => {
        button.addEventListener('click', this.onTabButtonClick.bind(this));
      });
    }

    onTabButtonClick(e) {
      e.preventDefault();
      this.tabButtons.forEach((button) => {
        button.classList.toggle(
          'is-active',
          button.dataset.tabId === e.target.dataset.tabId
        );
      });

      this.tabs.forEach((tab) => {
        tab.classList.toggle('is-active', tab.id === e.target.dataset.tabId);
      });
    }

    closeAnimation() {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          this.detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
